import { useState } from 'react';
import parrotStanding from '@/assets/lendo-parrot-standing.webp';
import { QuizCard } from '@/components/QuizCard';
import { ProgressBar } from '@/components/ProgressBar';
import { OptionButton } from '@/components/OptionButton';
import { ResultCard } from '@/components/ResultCard';
import { ParrotHelper } from '@/components/ParrotHelper';
import { FAQ } from '@/components/FAQ';
import { QuizAnswer, getRecommendation } from '@/types/quiz';
import { Percent, Gift, Shield, DollarSign, Calendar, Plane, ShoppingCart, Package, PiggyBank, MoreHorizontal, Receipt, CreditCard, Banknote, ArrowLeft, CheckCircle, Clock, TrendingDown, ArrowRight, TrendingUp } from 'lucide-react';
import { trackInternalLead } from '#common/utils/tracking.ts'

interface IndexProps {
  appName?: string;
}

const Index = ({ appName = 'credit-card-matcher' }: IndexProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer>({});
  const [showResults, setShowResults] = useState(false);
  const questions = [{
    id: 'question1',
    title: 'Hva er viktigst for deg i et kredittkort?',
    options: [{
      value: 'lav-rente',
      label: 'Lav rente ved nedbetaling',
      icon: <Percent className="w-5 h-5" />
    }, {
      value: 'cashback',
      label: 'Cashback (penger tilbake på kjøp)',
      icon: <DollarSign className="w-5 h-5" />
    }, {
      value: 'rabatter',
      label: 'Rabatter og fordelsprogrammer',
      icon: <Gift className="w-5 h-5" />
    }, {
      value: 'forsikring',
      label: 'Reiseforsikring inkludert',
      icon: <Shield className="w-5 h-5" />
    }, {
      value: 'lang-periode',
      label: 'Lang rentefri periode',
      icon: <Calendar className="w-5 h-5" />
    }, {
      value: 'hoy-grense',
      label: 'Høy kredittgrense',
      icon: <TrendingUp className="w-5 h-5" />
    }]
  }, {
    id: 'question2',
    title: 'Hvordan planlegger du å bruke kredittkortet mest?',
    options: [{
      value: 'reise',
      label: 'Reiser (fly, hotell, leiebil)',
      icon: <Plane className="w-5 h-5" />
    }, {
      value: 'netthandel',
      label: 'Netthandel og småkjøp',
      icon: <ShoppingCart className="w-5 h-5" />
    }, {
      value: 'store-innkjop',
      label: 'Store enkeltkjøp (møbler, elektronikk osv.)',
      icon: <Package className="w-5 h-5" />
    }, {
      value: 'buffer',
      label: 'Buffer til uforutsette utgifter',
      icon: <PiggyBank className="w-5 h-5" />
    }, {
      value: 'annet',
      label: 'Annet',
      icon: <MoreHorizontal className="w-5 h-5" />
    }]
  }, {
    id: 'question3',
    title: 'Hvordan håndterer du kredittkortregningen?',
    options: [{
      value: 'alltid-hele',
      label: 'Betaler alltid hele beløpet ved forfall',
      icon: <CheckCircle className="w-5 h-5" />
    }, {
      value: 'vanligvis-hele',
      label: 'Betaler vanligvis hele beløpet, men ønsker fleksibilitet',
      icon: <Clock className="w-5 h-5" />
    }, {
      value: 'over-tid',
      label: 'Betaler ofte ned over tid',
      icon: <TrendingDown className="w-5 h-5" />
    }]
  }];
  const handleAnswer = (questionId: string, value: string) => {
    const newAnswers = {
      ...answers,
      [questionId]: value
    };
    setAnswers(newAnswers);
    if (currentStep < questions.length - 1) {
      setTimeout(() => setCurrentStep(currentStep + 1), 150);
    } else {
      setTimeout(() => setShowResults(true), 150);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };

  // Get app-specific styling and content
  const getAppConfig = (appName: string) => {
    switch (appName) {
      default:
        return {
          title: 'Credit Card Matcher - Find Your Perfect Card',
          bgClass: 'bg-gradient-background',
          accentColor: 'text-primary'
        };
    }
  };

  const appConfig = getAppConfig(appName);

  if (showResults) {
    const {
      primary,
      secondary
    } = getRecommendation(answers);
    return <div className={`min-h-screen ${appConfig.bgClass}`}>

        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <button onClick={handleRestart} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Ta quizen på nytt
          </button>

          <div className="text-center mb-8 animate-fade-in">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              🎉 Her er anbefalingen din!
            </h1>
            <p className="text-lg text-muted-foreground">
              Basert på dine svar har vi funnet kredittkortet som passer deg best.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8 animate-scale-in">
            <ResultCard card={primary} isPrimary onClick={() => trackInternalLead(`cta: søk kredittkort ${primary.name}`, { isHighlighted: true })} />
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Nest beste valg</h3>
              <ResultCard card={secondary} onClick={() => trackInternalLead(`cta: søk kredittkort ${secondary.name}`, { isHighlighted: false })} />
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground bg-muted/50 p-4 rounded-xl mt-6">
            💡 Dette er en forenklet anbefaling. Les alltid vilkår før du søker.
          </div>

          <div className="bg-card border border-border rounded-2xl p-4 md:p-6 shadow-card mt-6 md:mt-8 animate-fade-in">
            <div className="flex items-start gap-4 md:gap-6">
              <img src={parrotStanding} alt="Lendo papegøye" className="w-20 h-20 md:w-32 md:h-32 object-contain flex-shrink-0 -mt-2" />
              <div>
                <h3 className="text-lg md:text-xl font-bold text-foreground mb-2 md:mb-3">Én søknad - flere muligheter</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-2 md:mb-3">Når du søker gjennom oss, slipper du å fylle ut søknader flere steder. Vi sender søknaden til flere banker samtidig. Dermed kan du få innvilget flere kredittkort, sammenligne vilkårene og velge det som passer deg best.</p>
                <a href="https://www.lendo.no/kredittkort/soknad/" target="_blank" onClick={() => trackInternalLead('cta: søk kredittkort')} rel="noopener noreferrer" className="text-primary hover:underline font-medium inline-flex items-center gap-1 text-sm md:text-base">
                  Søk kredittkort
                  <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  const currentQuestion = questions[currentStep];
  return <>
    <div className="bg-gradient-background pb-8 md:pb-12">
      <div className="container mx-auto px-4 py-4 md:py-8 max-w-2xl">
        {currentStep === 0 && <div className="mb-6 md:mb-8 animate-fade-in">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4 text-center">
              Finn det beste kredittkortet for deg
            </h1>
            <ParrotHelper message="Svar på tre enkle spørsmål – det tar under ett minutt." />
          </div>}

        <ProgressBar currentStep={currentStep + 1} totalSteps={questions.length} />

        <QuizCard className="animate-scale-in">
          {currentStep > 0 && <button onClick={handleBack} className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4 md:mb-6">
              <ArrowLeft className="w-4 h-4" />
              Tilbake
            </button>}

          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
            {currentQuestion.title}
          </h2>

          <div className="space-y-2 md:space-y-3">
            {currentQuestion.options.map(option => <OptionButton key={option.value} icon={option.icon} selected={answers[currentQuestion.id as keyof QuizAnswer] === option.value} onClick={() => handleAnswer(currentQuestion.id, option.value)}>
                {option.label}
              </OptionButton>)}
          </div>
        </QuizCard>
      </div>
    </div>
    <FAQ />
  </>;
};
export default Index;